import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { css } from "@emotion/react";
import { FormattedMessage } from "react-intl";
import Modal, { MODAL_SIZE } from "components/Modal";
import InterviewTitle from "./InterviewTitle";
import InterviewSubtitle from "./InterviewSubtitle";

/**
 * InterviewPreviewModal
 *
 * @param {Object}    interviewData
 * @param {function}  closeStateFunction
 */
const InterviewPreviewModal = ({ interviewData, closeStateFunction, ...props }) => {
  const [currentStep, setCurrentStep] = useState(0);

  const questions = interviewData?.questions?.filter((question) => question.enabled) || [];
  const introVideoUrl = interviewData?.introVideoUrl;

  const steps = useMemo(() => {
    const allSteps = [];

    if (introVideoUrl) {
      allSteps.push({
        type: "intro",
        titleId: interviewData?.name,
        subtitleId: "VideoPresentation.IntroStep.Subtitle",
        video: introVideoUrl,
      });
    }

    questions.forEach((question) => {
      allSteps.push({
        type: "question",
        titleId: "VideoPresentation.QuestionStep.Title",
        titleValues: {
          title: question.title,
        },
        label: (
          <FormattedMessage
            id="VideoPresentation.QuestionStep.Label"
            values={{ current: introVideoUrl ? currentStep : currentStep + 1, total: questions.length }}
          />
        ),
        description: question.description,
        descriptionJson: question.descriptionJson,
        video: question.video,
        duration: question.timeLimitSeconds,
      });
    });

    return allSteps;
  }, [interviewData, introVideoUrl, questions, currentStep]);

  const step = steps[currentStep];

  const isLastStep = currentStep === steps.length - 1;
  const isIntro = step?.type === "intro";

  const handleNext = () => {
    if (!isLastStep) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handleClose = () => {
    closeStateFunction(false);
    setCurrentStep(0);
  };

  const primaryButtonMessageId = isIntro
    ? "Missions.Roles.RoleVetting.Interviews.StartInterview"
    : "Missions.Roles.RoleVetting.Interviews.NextQuestion";

  if (!step) return null;

  return (
    <Modal
      title={interviewData?.name}
      size={MODAL_SIZE.large}
      showSave={!isLastStep}
      primaryButtonMessageId={primaryButtonMessageId}
      onSubmit={isLastStep ? handleClose : handleNext}
      canSubmit={true}
      secondaryButtonMessageId="Global.Close"
      onClose={handleClose}
      {...props}
    >
      <div css={styles.container}>
        <div css={styles.video_section}>
          {step.video ? (
            <video src={step.video} preload="metadata" css={styles.video} autoPlay />
          ) : (
            <div css={styles.empty_video}>
              <FormattedMessage id="Video.VideoThumbnail.NotAvailable" />
            </div>
          )}
        </div>

        <div css={styles.content_section}>
          <InterviewTitle titleId={step.titleId} values={step.titleValues} label={step.label} />
          <InterviewSubtitle
            key={currentStep} // Force remount to reset any internal state when changing steps
            subtitleId={step.subtitleId}
            description={step.description}
            descriptionJson={step.descriptionJson}
          />
        </div>
      </div>
    </Modal>
  );
};

const styles = {
  container: css`
    display: flex;
    min-height: 420px;
    background: #fff;
  `,
  video_section: css`
    width: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
  `,
  content_section: css`
    width: 50%;
    padding: 32px 28px;
    gap: 12px;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
  `,
  video: css`
    width: 100%;
    border-radius: 16px;
    background: #000;
    aspect-ratio: 4 / 3;
  `,
  empty_video: css`
    width: 100%;
    aspect-ratio: 4 / 3;
    border-radius: 16px;
    background: #f4f4f7;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #999;
  `,
  label: css`
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 16px;
    color: #2d1457;
  `,
  title: css`
    font-size: 36px;
    line-height: 1.15;
    margin-bottom: 16px;
    color: #1f1147;
  `,
  description: css`
    font-size: 15px;
    line-height: 1.6;
    color: #666;
    margin-bottom: 24px;
  `,
  meta: css`
    font-size: 14px;
    color: #888;
    margin-bottom: 32px;
  `,
};

InterviewPreviewModal.propTypes = {
  interviewData: PropTypes.object,
  closeStateFunction: PropTypes.func.isRequired,
};

export default InterviewPreviewModal;
